import { type FormEvent, type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import type { ApiError, RiderCashBlocksView, RiderCashReleaseView } from '@vello/api-client';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyState, Field, KeyValueList, Notice, PageHeader, Panel, Select, Table, TextInput, Stack, Text, PanelHead } from '@vello/web-theme';
import {
  maySelfApprove,
  CASH_RELEASE_REASONS,
  cashBlockAuthority,
  cashBlockRows,
  formatMarketInstant,
  marketTimeZone,
  releaseTone,
  tailOf,
  type CashBlockRowView,
} from '../../src/index';
import { SelfApprovalNote } from '../components/self-approval-note';
import { useAdmin } from '../session';
import { PersonName, usePersonNames } from '../components/person-name';
import { IdRef } from '../components/code-ref';
import { DrawerStack } from '../components/drawer-stack';
import { RevealContact } from '../components/reveal-contact';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useRouter } from '../router';

/**
 * Finance > **Bloqueio de dinheiro** (D536 AN-RIDER-DEBTS; D501 / Q-010) - the riders blocked from paying in cash (two recorded non-payments, the governed threshold), and the release of the
 * block under FOUR EYES with the existing routes: one operator proposes (`rider_cash:propose`, a reason and an optional note), ANOTHER approves or rejects (`rider_cash:approve`).
 *
 *  - The list is the LIVE eligibility the ride request enforces (from the transactional side), with the names from the analytics store - so it needs the personal word
 *    (`analytics:view_personal`) on top of `rider_cash:view`, and the server logs each read. Without both it is not drawn.
 *  - Each row says how many recorded non-payments count, when the last was recorded, and - if a release is waiting - who proposed it and when. The decision is NOT offered on one's own
 *    proposal (the console says why; the server refuses it too). Approving clears the shortfalls of the market up to the decision; it is NOT a waiver: the money owed stays on the debt.
 *  - Below, the market's latest releases of every status: who proposed, who decided, and when.
 *  - A refused read or write is an ERROR in words, never an empty list.
 */
export function RiderCashBlocksScreen(): ReactElement {
  const { runtime, markets, grants, me } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { v, fail, explain } = useVocab();
  const { params, navigate } = useRouter();
  const { mayView, mayPropose, mayApprove } = cashBlockAuthority(grants);
  // Owner 2026-10-12: the market IS the header scope's city — no second market selector on this screen. A market handed
  // over by the router (the "release" action of a rider's debt history) is a navigation target, not a selector.
  const { market: scopeMarket, choices } = useScopeMarket();
  const effectiveMarket = params.market ?? scopeMarket;
  const market = effectiveMarket ?? '';
  const [loaded, setLoaded] = useState('');
  const [view, setView] = useState<RiderCashBlocksView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  /** The rider whose block is open in the drawer (the record, the proposal form and the decision all live there). */
  const [openRider, setOpenRider] = useState<string | undefined>(undefined);
  const [reason, setReason] = useState<string>(CASH_RELEASE_REASONS[0]);
  const [note, setNote] = useState('');
  const [decisionReason, setDecisionReason] = useState('');
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  const operatorId = me?.id;

  const refusal = useCallback((e: ApiError): string => explain(e) ?? t('riderDebts.error', { code: fail(e) }), [t, fail, explain]);

  const load = useCallback(async (m: string): Promise<void> => {
    const mine = ++seq.current;
    const target = m.trim();
    setLoaded(target);
    if (target === '' || !mayView) {
      setError(undefined);
      return;
    }
    const res = await runtime.riderDebts.cashBlocks(target);
    if (mine !== seq.current) return;
    if (res.ok) {
      setView(res.value);
      setError(undefined);
    } else setError(refusal(res.error));
  }, [runtime, mayView, refusal]);

  // The list follows the scope's (or the handed-over) market by itself — the skeleton returns only on a market change —
  // and stays current in the background; there is no "load" button.
  useEffect(() => {
    setView(undefined);
    setError(undefined);
    void load(market);
  }, [market, load]);
  useAutoRefresh(() => load(market), { intervalMs: AUTO_REFRESH_MS.queue, enabled: mayView && market.trim() !== '' });

  const tz = marketTimeZone(loaded, markets);
  const rows = view === undefined ? [] : cashBlockRows(view, operatorId, { mayPropose, mayApprove, selfApproval });
  // A name where the operator may read it (one batched lookup), else the short tail of the id; the signed-in operator is "you".
  const nameOf = usePersonNames([...(view?.releases ?? []).flatMap((r) => [r.proposedBy, r.decidedBy]), ...rows.map((r) => r.pending?.proposedBy)]);
  const who = (id: string): string => (operatorId !== undefined && id.toLowerCase() === operatorId.toLowerCase() ? t('cashBlock.you') : (nameOf(id) ?? tailOf(id)));
  const drawerRow = openRider !== undefined ? rows.find((r) => r.block.riderId === openRider) : undefined;

  const closeDrawer = (): void => {
    setOpenRider(undefined);
    setNote('');
    setDecisionReason('');
  };

  const propose = async (e: FormEvent<HTMLFormElement>, row: CashBlockRowView): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setMsg(undefined);
    const res = await runtime.riderCashReleases.propose({ riderId: row.block.riderId, marketId: loaded, reasonCode: reason, ...(note.trim() !== '' ? { note: note.trim() } : {}) });
    setBusy(false);
    if (res.ok) {
      setMsg({ tone: 'success', text: t('cashBlock.done.proposed') });
      setNote('');
      await load(loaded);
    } else setMsg({ tone: 'danger', text: refusal(res.error) });
  };

  const decide = async (row: CashBlockRowView, decision: 'approve' | 'reject'): Promise<void> => {
    const pending = row.pending;
    if (pending === null) return;
    setBusy(true);
    setMsg(undefined);
    const res = await runtime.riderCashReleases.decide(pending.releaseId, decision, { marketId: loaded, ...(decisionReason.trim() !== '' ? { reasonCode: decisionReason.trim() } : {}) });
    setBusy(false);
    if (res.ok) {
      setMsg({ tone: 'success', text: t(decision === 'approve' ? 'cashBlock.done.approved' : 'cashBlock.done.rejected') });
      closeDrawer();
      await load(loaded);
    } else setMsg({ tone: 'danger', text: refusal(res.error) });
  };

  const releaseColumns = [
    { key: 'status', header: t('cashBlock.col.status'), render: (r: RiderCashReleaseView) => <Badge tone={releaseTone(r.status)}>{v('payments.rider_cash_release_status', r.status)}</Badge> },
    { key: 'rider', header: t('cashBlock.col.rider'), render: (r: RiderCashReleaseView) => <PersonName id={r.riderId} /> },
    { key: 'reason', header: t('cashBlock.col.reason'), render: (r: RiderCashReleaseView) => <span>{v('payments.rider_cash_release_reason', r.reasonCode)}</span> },
    { key: 'proposed', header: t('cashBlock.col.proposed'), render: (r: RiderCashReleaseView) => <span className="tnum">{t('cashBlock.by', { who: who(r.proposedBy), when: formatMarketInstant(r.proposedAt, tz, lang) })}</span> },
    { key: 'decided', header: t('cashBlock.col.decided'), render: (r: RiderCashReleaseView) => (r.decidedBy === null || r.decidedAt === null ? <Text role="caption" tone="tertiary">—</Text> : <span className="tnum">{t('cashBlock.by', { who: who(r.decidedBy), when: formatMarketInstant(r.decidedAt, tz, lang) })}<SelfApprovalNote record={r} /></span>) },
    { key: 'cleared', header: t('cashBlock.col.cleared'), numeric: true, render: (r: RiderCashReleaseView) => <span className="tnum">{r.occurrencesCleared ?? '—'}</span> },
  ];

  return (
    <>
      <PageHeader title={t('cashBlock.title')} subtitle={t('cashBlock.subtitle')} />
      {!mayView ? (
        <Panel><EmptyState title={t('cashBlock.noPermission')} /></Panel>
      ) : effectiveMarket === undefined ? (
        <ScopeMarketNotice choices={choices} />
      ) : (
        <>
          <Panel>
            <Stack gap={4}>
              {error !== undefined && <Notice tone="danger">{error}</Notice>}
              {msg !== undefined && drawerRow === undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
              {view !== undefined && <Text as="p" role="caption" tone="tertiary" mono>{t('cashBlock.rule', { after: String(view.rule.blockAfter), policy: view.rule.policyVersion })}</Text>}
              {view !== undefined && <Text as="p" role="caption" tone="tertiary">{t('cashBlock.notWaiver')}</Text>}
              {view?.truncated === true && <Notice tone="warning">{t('cashBlock.truncated')}</Notice>}
            </Stack>
          </Panel>

          <Panel padded={false}>
            <Table
              columns={[
                { key: 'rider', header: t('cashBlock.col.rider'), render: (r: CashBlockRowView) => <PersonName id={r.block.riderId} fallback={r.block.name ?? t('riderDebts.unnamed')} /> },
                { key: 'counted', header: t('cashBlock.col.counted'), numeric: true, render: (r: CashBlockRowView) => <span className="tnum">{r.block.counted}</span> },
                { key: 'last', header: t('cashBlock.col.last'), render: (r: CashBlockRowView) => <span className="tnum">{formatMarketInstant(r.block.lastShortfallAt, tz, lang)}</span> },
                { key: 'pending', header: t('cashBlock.col.pending'), render: (r: CashBlockRowView) => (
                  r.pending === null ? <Text role="caption" tone="tertiary">—</Text> : (
                    <Stack>
                      <Badge tone="warning">{v('payments.rider_cash_release_status', 'PENDING')}</Badge>
                      <Text role="caption" tone="tertiary" mono>{t('cashBlock.by', { who: who(r.pending.proposedBy), when: formatMarketInstant(r.pending.proposedAt, tz, lang) })}</Text>
                      <Text role="caption" tone="tertiary" mono>{v('payments.rider_cash_release_reason', r.pending.reasonCode)}</Text>
                    </Stack>
                  )
                ) },
              ]}
              rows={rows}
              rowKey={(r) => r.block.riderId}
              onRowClick={(r) => { setOpenRider(r.block.riderId); setMsg(undefined); }}
              {...(openRider !== undefined ? { selectedKey: openRider } : {})}
              loading={loaded !== '' && view === undefined && error === undefined}
              loadingLabel={t('ui.loading')}
              error={error !== undefined ? t('ui.loadFailed') : undefined}
              onRetry={() => void load(market)}
              retryLabel={t('ui.retry')}
              empty={<EmptyState title={loaded === '' ? t('debts.needsMarket') : t('cashBlock.empty')} />}
            />
          </Panel>

          <Drawer open={drawerRow !== undefined} onClose={closeDrawer} labelledBy="cb-drawer-title">
            {drawerRow !== undefined && (
              <>
                <DrawerHeader
                  titleId="cb-drawer-title"
                  title={<PersonName id={drawerRow.block.riderId} fallback={drawerRow.block.name ?? t('riderDebts.unnamed')} />}
                  subtitle={drawerRow.pending === null ? undefined : <Badge tone="warning">{v('payments.rider_cash_release_status', 'PENDING')}</Badge>}
                  onClose={closeDrawer}
                  closeLabel={t('ui.close')}
                />
                <DrawerBody>
                  <DrawerStack>
                    {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
                    <KeyValueList
                      items={[
                        { label: t('cashBlock.col.rider'), value: <IdRef value={drawerRow.block.riderId} copy full /> },
                        { label: t('cashBlock.col.phone'), value: <RevealContact userId={drawerRow.block.riderId} masked={drawerRow.block.phoneMasked ?? undefined} /> },
                        { label: t('cashBlock.col.counted'), value: String(drawerRow.block.counted), mono: true },
                        { label: t('cashBlock.col.last'), value: formatMarketInstant(drawerRow.block.lastShortfallAt, tz, lang), mono: true },
                        ...(drawerRow.pending !== null
                          ? [
                              { label: t('cashBlock.col.pending'), value: t('cashBlock.by', { who: who(drawerRow.pending.proposedBy), when: formatMarketInstant(drawerRow.pending.proposedAt, tz, lang) }), mono: true },
                              { label: t('cashBlock.col.reason'), value: v('payments.rider_cash_release_reason', drawerRow.pending.reasonCode) },
                            ]
                          : []),
                      ]}
                    />
                    <Text as="p" role="caption" tone="tertiary">{t('cashBlock.notWaiver')}</Text>
                    {drawerRow.ownProposal && <Notice tone="neutral">{t('cashBlock.ownProposal')}</Notice>}
                    {drawerRow.mayPropose && (
                      <form id="cb-propose" onSubmit={(e) => void propose(e, drawerRow)} aria-label={t('cashBlock.propose.title')}>
                        <Stack gap={4}>
                          <Text as="h2" role="title">{t('cashBlock.propose.title')}</Text>
                          <Field label={t('cashBlock.propose.reason')} htmlFor="cb-reason">
                            <Select id="cb-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
                              {CASH_RELEASE_REASONS.map((r) => <option key={r} value={r}>{v('payments.rider_cash_release_reason', r)}</option>)}
                            </Select>
                          </Field>
                          <Field label={t('cashBlock.propose.note')} htmlFor="cb-note" hint={t('cashBlock.propose.note.hint')}>
                            <TextInput id="cb-note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
                          </Field>
                        </Stack>
                      </form>
                    )}
                    {drawerRow.mayDecide && (
                      <Field label={t('cashBlock.decisionReason')} htmlFor="cb-decision-reason" hint={t('cashBlock.decisionReason.hint')}>
                        <TextInput id="cb-decision-reason" value={decisionReason} maxLength={64} placeholder="CASH_BLOCK_RELEASE_APPROVED" onChange={(e) => setDecisionReason(e.target.value.toUpperCase())} />
                      </Field>
                    )}
                  </DrawerStack>
                </DrawerBody>
                <DrawerFooter>
                  <Button variant="ghost" onClick={() => navigate('finance_rider_debts', { market: loaded })}>{t('cashBlock.action.debts')}</Button>
                  {drawerRow.mayDecide && <Button variant="danger" disabled={busy} onClick={() => void decide(drawerRow, 'reject')}>{t('cashBlock.action.reject')}</Button>}
                  {drawerRow.mayDecide && <Button variant="secondary" disabled={busy} onClick={() => void decide(drawerRow, 'approve')}>{t('cashBlock.action.approve')}</Button>}
                  {drawerRow.mayPropose && <Button type="submit" form="cb-propose" variant="primary" disabled={busy}>{t('cashBlock.propose.submit')}</Button>}
                </DrawerFooter>
              </>
            )}
          </Drawer>

          <Panel padded={false}>
            <PanelHead title={t('cashBlock.releases.title')} />
            <Table columns={releaseColumns} rows={view?.releases ?? []} rowKey={(r) => r.releaseId} empty={<EmptyState title={t('cashBlock.releases.empty')} />} />
          </Panel>
        </>
      )}
    </>
  );
}
